// BUILD-042 (Oct 16) — the properties editor for ONE selected field, and for ONE initials rule, in the expanded-fields "Set up signing" screen.
// Plain controls only. HR never writes a pattern: validation is a short list of reviewed presets (the server holds the actual rules).
// A field whose value comes from the employee record, fixed text or HR at assignment time shows exactly that, and nothing stale survives a change.
import { useState } from 'react';
import type { HrEditorRules } from '../../api/client';
import { DateEntry } from './DateEntry';
import {
  KIND_LABEL, SOURCE_LABEL, sourceChoices, withSource, parsePageList, formatPageList, initialsPageList, MAX_DROPDOWN_OPTIONS, MULTILINE_LIMITS,
  READ_ONLY_EXPLANATION, STARTING_ANSWER_EXPLANATION, legacyValueProblem, makeReadOnly, makeStartingAnswer, dropdownToEmployeeChoice, dropdownToReadOnlyText,
  type UiField, type UiInitialsSet,
} from './formFieldsModel';

const inputCls = 'w-full border border-gray-300 rounded px-2 py-1 text-xs';
const labelCls = 'block text-[11px] font-medium text-gray-600 mb-0.5';

export function FieldProperties({
  field, group, rules, onChange, onChangeGroup, onAddOption, onDelete, onDeleteGroup,
}: {
  field: UiField;
  /** Every option of the same choice group (radio only). */
  group: UiField[];
  rules: HrEditorRules;
  onChange: (next: UiField) => void;
  onChangeGroup: (patch: { required?: boolean; groupKey?: string }) => void;
  onAddOption: () => void;
  onDelete: () => void;
  onDeleteGroup: () => void;
}) {
  const f = field;
  const sources = sourceChoices(f.kind);
  const set = (patch: Partial<UiField>) => onChange({ ...f, ...patch });
  return (
    <div className="space-y-2.5 text-xs text-gray-700" data-testid="field-properties">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-gray-800">{KIND_LABEL[f.kind]}{f.origin?.kind === 'imported' ? <span className="ml-1 font-normal text-gray-400">· imported</span> : null}</div>
        <button type="button" className="underline text-red-700" onClick={onDelete} data-testid="delete-field">Remove {f.kind === 'radio' ? 'this option' : 'field'}</button>
      </div>

      <div>
        <label className={labelCls} htmlFor="fp-label">Label the employee sees</label>
        <input id="fp-label" className={inputCls} value={f.label} maxLength={120} onChange={(e) => set({ label: e.target.value })} data-testid="fp-label" />
      </div>

      {f.kind !== 'radio' && (
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={f.required || (f.kind === 'text' && f.source !== 'employee')} disabled={f.kind === 'signature' || (f.kind === 'text' && f.source !== 'employee')} onChange={(e) => set({ required: e.target.checked })} data-testid="fp-required" />
          <span>Required{f.kind === 'signature' ? ' (a signature is always required)' : f.kind === 'text' && f.source !== 'employee' ? ' (always, because HR fills this in)' : ''}</span>
        </label>
      )}

      {f.kind === 'text' && f.text && (
        <>
          <div>
            <label className={labelCls} htmlFor="fp-validation">What can be typed</label>
            <select id="fp-validation" className={inputCls} value={f.text.validation}
              onChange={(e) => {
                const v = e.target.value;
                // The HR date is always locked (the employee sees it and cannot retype it) and needs room for MM/DD/YYYY.
                if (v === 'dateUS') set({ text: { ...f.text!, validation: v, multiline: false, maxLength: Math.max(10, f.text!.maxLength) }, locked: true, ...(f.source === 'assignment' ? { required: true } : {}) });
                else set({ text: { ...f.text!, validation: v } });
              }} data-testid="fp-validation">
              {/* The date format is for values HR enters; an employee-typed date is not offered (the signing provider's date handling is unproven). */}
              {Object.entries(rules.textValidations).filter(([k]) => k !== 'dateUS' || f.source === 'assignment' || f.source === 'fixed').map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
            {f.text.validation === 'dateUS' && <p className="text-gray-500 mt-0.5" data-testid="fp-date-help">HR enters a real date as MM/DD/YYYY (typed or from a calendar) {f.source === 'fixed' ? 'below' : 'each time the form is assigned'}. It is locked on the form, separate from “Date signed”, and is not copied to the employee record or payroll.</p>}
          </div>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className={labelCls} htmlFor="fp-maxlen">Longest answer (characters)</label>
              <input id="fp-maxlen" type="number" min={1} max={rules.maxTextLength} className={inputCls} value={f.text.maxLength}
                onChange={(e) => set({ text: { ...f.text!, maxLength: Math.max(1, Math.min(rules.maxTextLength, Number(e.target.value) || 1)) } })} data-testid="fp-maxlen" />
            </div>
          </div>
          <label className="flex items-start gap-2 opacity-90">
            <input type="checkbox" checked={f.text.multiline} disabled={!rules.multilineConfirmed || (f.source !== 'employee' && !f.text.multiline)}
              onChange={(e) => set({ text: { ...f.text!, multiline: e.target.checked, ...(e.target.checked ? { validation: 'none', maxLength: Math.min(f.text!.maxLength, (rules.multilineLimits ?? MULTILINE_LIMITS).maxCharacters) } : {}) } })}
              aria-describedby="fp-multiline-help" data-testid="fp-multiline" />
            <span>
              Several lines
              {rules.multilineConfirmed
                ? <span id="fp-multiline-help" className="block text-gray-500">Up to {(rules.multilineLimits ?? MULTILINE_LIMITS).maxLines} lines and {(rules.multilineLimits ?? MULTILINE_LIMITS).maxCharacters} characters in total, in a box at least 5 x 1.4 inches, filled in by the employee. This is the size the signing provider has been tested with; the provider does not enforce the line limit for you.</span>
                : <em id="fp-multiline-help" className="block text-amber-700 not-italic">Not available. This box takes a single line.</em>}
            </span>
          </label>
        </>
      )}

      {sources.length > 1 && (
        <div>
          <label className={labelCls} htmlFor="fp-source">Where the value comes from</label>
          <select id="fp-source" className={inputCls} value={f.source} onChange={(e) => onChange(withSource(f, e.target.value as UiField['source']))} data-testid="fp-source">
            {sources.map((s) => <option key={s} value={s}>{SOURCE_LABEL[s]}</option>)}
          </select>
        </div>
      )}

      {f.kind === 'text' && f.source === 'profile' && (
        <div>
          <label className={labelCls} htmlFor="fp-profile">Which employee detail</label>
          <select id="fp-profile" className={inputCls} value={f.profileKey ?? 'employeeName'} onChange={(e) => set({ profileKey: e.target.value as 'employeeName' | 'employeeWorkEmail' })} data-testid="fp-profile">
            <option value="employeeName">Employee name</option>
            <option value="employeeWorkEmail">Work email</option>
          </select>
          <p className="text-gray-500 mt-0.5">Only these two details can be filled in automatically. They are copied when the form is assigned and then frozen for that assignment.</p>
        </div>
      )}
      {f.kind === 'text' && f.source === 'fixed' && (
        <div>
          <label className={labelCls} htmlFor="fp-fixed">{f.text?.validation === 'dateUS' ? 'Fixed date' : 'Fixed text'}</label>
          {f.text?.validation === 'dateUS'
            ? <DateEntry id="fp-fixed" value={f.fixedValue ?? ''} onChange={(next) => set({ fixedValue: next })} testId="fp-fixed" />
            : <input id="fp-fixed" className={inputCls} value={f.fixedValue ?? ''} maxLength={f.text?.maxLength ?? 1000} onChange={(e) => set({ fixedValue: e.target.value })} data-testid="fp-fixed" />}
        </div>
      )}
      {f.kind === 'text' && f.source !== 'employee' && f.locked === true && (
        <div className="rounded border border-sky-200 bg-sky-50 px-2 py-1 text-sky-900" data-testid="fp-readonly">
          <span className="font-medium">Read-only for the employee.</span> {READ_ONLY_EXPLANATION}{f.text?.validation === 'dateUS' ? ' A date HR enters is always final.' : ''}
        </div>
      )}
      {f.kind === 'text' && f.source !== 'employee' && f.locked !== true && (
        <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-amber-900 space-y-1" data-testid="fp-legacy">
          <div>{legacyValueProblem(f)}</div>
          <div className="flex gap-3 flex-wrap">
            <button type="button" className="underline" onClick={() => onChange(makeReadOnly(f))} data-testid="fp-make-readonly">Make it read-only (HR's value is final)</button>
            <button type="button" className="underline" onClick={() => onChange(makeStartingAnswer(f))} data-testid="fp-make-starting">Make it a starting answer the employee can change</button>
          </div>
        </div>
      )}
      {f.kind === 'text' && f.source === 'employee' && (
        <div>
          <label className={labelCls} htmlFor="fp-starting">Starting answer (optional — the employee can change it)</label>
          <input id="fp-starting" className={inputCls} value={f.startingValue ?? ''} maxLength={f.text?.maxLength ?? 1000}
            onChange={(e) => { const v = e.target.value; const next = { ...f }; if (v) next.startingValue = v; else delete next.startingValue; onChange(next); }} data-testid="fp-starting" />
          <p className="text-gray-500 mt-0.5" data-testid="fp-starting-help">{STARTING_ANSWER_EXPLANATION}</p>
        </div>
      )}
      {f.source === 'assignment' && <p className="text-gray-500">HR will be asked for this value each time the form is assigned. It is frozen with that assignment.</p>}

      {f.kind === 'dropdown' && f.dropdown && (
        <>
          <div>
            <label className={labelCls} htmlFor="fp-options">Choices (one per line)</label>
            <textarea id="fp-options" rows={4} className={inputCls} value={f.dropdown.options.join('\n')}
              onChange={(e) => {
                const options = e.target.value.split('\n').slice(0, MAX_DROPDOWN_OPTIONS);
                const keep = (v?: string) => (v && options.includes(v) ? v : undefined);
                set({ dropdown: { options, defaultValue: keep(f.dropdown!.defaultValue) }, ...(f.source !== 'employee' && f.fixedValue ? { fixedValue: keep(f.fixedValue) } : {}) });
              }} data-testid="fp-options" />
          </div>
          {f.source !== 'employee' ? (
            <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-amber-900 space-y-1" data-testid="fp-legacy-dropdown">
              <div>{legacyValueProblem(f)}</div>
              <div className="flex gap-3 flex-wrap">
                <button type="button" className="underline" onClick={() => onChange(dropdownToEmployeeChoice(f))} data-testid="fp-dd-to-employee">Let the employee choose (start on the choice HR set)</button>
                <button type="button" className="underline" onClick={() => onChange(dropdownToReadOnlyText(f))} data-testid="fp-dd-to-readonly-text">Show the choice as read-only text</button>
              </div>
            </div>
          ) : f.source === 'employee' ? (
            <div>
              <label className={labelCls} htmlFor="fp-dd-default">Starting choice (the employee can change it)</label>
              <select id="fp-dd-default" data-testid="fp-dd-default" className={inputCls} value={f.dropdown.defaultValue ?? ''} onChange={(e) => set({ dropdown: { ...f.dropdown!, defaultValue: e.target.value || undefined } })}>
                <option value="">None</option>
                {f.dropdown.options.filter((o) => o.trim()).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          ) : null}
          <p className="text-gray-500" data-testid="fp-dd-note">A dropdown is always the employee's choice (it can start on a choice you pick). The signing provider can only make text boxes read-only, so use a read-only text box if the employee must not change a value.</p>
        </>
      )}

      {f.kind === 'radio' && f.radio && (
        <>
          <div>
            <label className={labelCls} htmlFor="fp-option">This option is called</label>
            <input id="fp-option" className={inputCls} value={f.radio.optionValue} maxLength={80} onChange={(e) => onChange({ ...f, label: e.target.value, radio: { ...f.radio!, optionValue: e.target.value } })} data-testid="fp-option" />
          </div>
          <div>
            <label className={labelCls} htmlFor="fp-group">Choice group name (the employee picks one)</label>
            <input id="fp-group" className={inputCls} value={f.radio.groupKey} maxLength={64} onChange={(e) => onChangeGroup({ groupKey: e.target.value.replace(/[^A-Za-z0-9_]/g, '_') })} data-testid="fp-group" />
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={f.required} onChange={(e) => onChangeGroup({ required: e.target.checked })} data-testid="fp-required" />
            <span>An answer is required</span>
          </label>
          <div className="flex gap-3">
            <button type="button" className="underline text-[#1d6f9f]" onClick={onAddOption} data-testid="add-option">Add another option</button>
            <button type="button" className="underline text-red-700" onClick={onDeleteGroup} data-testid="delete-group">Remove the whole group ({group.length})</button>
          </div>
        </>
      )}

      {f.kind === 'checkbox' && <p className="text-gray-500">The employee ticks this box. Tick it as required to make agreement mandatory.</p>}
      {f.kind === 'dateSigned' && <p className="text-gray-500">Filled in automatically with the date the employee signs.</p>}
      <p className="text-gray-400">Field name: {f.fieldKey}. Drag the box on the page to move it, drag its corner to resize, or use the arrow keys.</p>
    </div>
  );
}

export function InitialsProperties({
  set, pageCount, selectedPage, onChange, onDelete, onSelectPage, onResetPage, onApplyToAll,
}: {
  set: UiInitialsSet;
  pageCount: number;
  /** The page currently shown (for per-page actions). */
  selectedPage: number;
  onChange: (next: UiInitialsSet) => void;
  onDelete: () => void;
  onSelectPage: (page: number) => void;
  /** Put this page's box back at the shared position. */
  onResetPage: (page: number) => void;
  /** Make the box on this page the shared position for every initialed page. */
  onApplyToAll: (page: number) => void;
}) {
  const pages = initialsPageList(set, pageCount);
  const [pagesText, setPagesText] = useState(set.pages === 'all' ? '' : formatPageList(set.pages));
  const [exText, setExText] = useState(formatPageList(set.excludePages));
  const [pagesErr, setPagesErr] = useState<string | null>(null);
  const [exErr, setExErr] = useState<string | null>(null);
  const moved = new Set(set.overrides.map((o) => o.page));
  return (
    <div className="space-y-2.5 text-xs text-gray-700" data-testid="initials-properties">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-gray-800">Initials on {pages.length} page{pages.length === 1 ? '' : 's'}</div>
        <button type="button" className="underline text-red-700" onClick={onDelete} data-testid="delete-initials">Remove initials</button>
      </div>
      <div>
        <label className={labelCls} htmlFor="ip-label">Label</label>
        <input id="ip-label" className={inputCls} value={set.label} maxLength={120} onChange={(e) => onChange({ ...set, label: e.target.value })} />
      </div>
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={set.required} onChange={(e) => onChange({ ...set, required: e.target.checked })} data-testid="ip-required" />
        <span>Required on every initialed page</span>
      </label>
      <fieldset>
        <legend className={labelCls}>Which pages</legend>
        <label className="flex items-center gap-2"><input type="radio" name="ip-pages" checked={set.pages === 'all'} onChange={() => { setPagesErr(null); onChange({ ...set, pages: 'all' }); }} data-testid="ip-all" /> Every page</label>
        <label className="flex items-center gap-2"><input type="radio" name="ip-pages" checked={set.pages !== 'all'} onChange={() => onChange({ ...set, pages: pages.length ? pages : [1] })} data-testid="ip-selected" /> Only the pages I list</label>
        {set.pages !== 'all' && (
          <>
            <input className={`${inputCls} mt-1`} value={pagesText} placeholder="for example 1, 3-5" data-testid="ip-pages-text"
              onChange={(e) => {
                setPagesText(e.target.value);
                const r = parsePageList(e.target.value, pageCount);
                if (r.ok) { setPagesErr(null); onChange({ ...set, pages: r.pages }); } else setPagesErr(r.error);
              }} />
            {pagesErr && <div className="text-amber-800 mt-0.5">{pagesErr}</div>}
          </>
        )}
      </fieldset>
      <div>
        <label className={labelCls} htmlFor="ip-ex">Skip these pages (optional)</label>
        <input id="ip-ex" className={inputCls} value={exText} placeholder="for example 2" data-testid="ip-exclude-text"
          onChange={(e) => {
            setExText(e.target.value);
            if (!e.target.value.trim()) { setExErr(null); onChange({ ...set, excludePages: [] }); return; }
            const r = parsePageList(e.target.value, pageCount);
            if (r.ok) { setExErr(null); onChange({ ...set, excludePages: r.pages }); } else setExErr(r.error);
          }} />
        {exErr && <div className="text-amber-800 mt-0.5">{exErr}</div>}
      </div>
      <div>
        <div className={labelCls}>Preview — the initials box on each page</div>
        <div className="flex flex-wrap gap-1" data-testid="ip-page-chips">
          {pages.map((p) => (
            <button key={p} type="button" onClick={() => onSelectPage(p)} data-testid={`ip-page-${p}`}
              className={`px-1.5 py-0.5 rounded border ${p === selectedPage ? 'border-amber-400 bg-amber-50' : 'border-gray-300'} ${moved.has(p) ? 'font-semibold' : ''}`}
              title={moved.has(p) ? 'Moved on this page' : 'At the shared position'}>
              {p}{moved.has(p) ? '*' : ''}
            </button>
          ))}
        </div>
        <p className="text-gray-500 mt-1">Drag the initials box on any page to move it on that page only (marked *). Pages with * differ from the shared position.</p>
      </div>
      {pages.includes(selectedPage) && (
        <div className="flex gap-3 flex-wrap">
          {moved.has(selectedPage) && <button type="button" className="underline text-[#1d6f9f]" onClick={() => onResetPage(selectedPage)} data-testid="ip-reset-page">Put page {selectedPage} back at the shared position</button>}
          <button type="button" className="underline text-[#1d6f9f]" onClick={() => onApplyToAll(selectedPage)} data-testid="ip-apply-all">Use page {selectedPage}'s position on every initialed page</button>
        </div>
      )}
    </div>
  );
}
